import type { AppConfig } from '@/types/config'

export const heardConfig: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    appUrl: import.meta.env.VITE_APP_URL,
    version: 'v1'
  },
  app: {
    id: 'heard',
    name: 'Heard Plus',
    description: 'Your personal audio library',
    contentId: 1,
    subscriptionPlanId: 1,
    logo: {
      default: '/assets/images/heard/logo.svg',
      icon: '/assets/images/heard/icon.svg'
    }
  },

  theme: {
    colors: {
      primary: '#7209B7',
      secondary: '#666666',
      accent: '#FF4081',
      background: '#F3F4F6'
    },
    dark: {
      primary: '#FFFFFF',
      secondary: '#E5E5E5',
      accent: '#FF4081',
      background: '#111827'
    },
    fonts: {
      heading: 'Mulish',
      body: 'Mulish'
    },
    form: {
      padding: {
        container: 'py-8 px-4 sm:px-10',
        field: 'space-y-4'
      },
      rounding: {
        button: 'rounded-md',
        input: 'rounded-md'
      }
    }
  },

  layout: {
    maxWidth: '2xl',
    headerHeight: '16',
    playerHeight: '72px',
    screens: {
      home: [
        {
          "type": "slider",
          "layout": "slider",
          "id": "1",
          "data": {
            "title": "",
            "list": [],
            "params": {
              "config": {
                "lazyload": false
              }
            }
          }
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Exclusives & Originals",
            "list": [],
            "params": {
              "filter[category]": "exclusive"
            }
          },
          "title": "Exclusives & Originals",
          "limit": 20
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "New Releases",
            "list": [],
            "params": {
              "filter[category]": "new"
            }
          },
          "title": "New Releases"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Included in Subscription",
            "list": [],
            "params": {
              "filter[is_subscription]": 1
            }
          },
          "title": "Included in Subscription"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Podcasts",
            "list": [],
            "params": {
              "filter[tag]": "podcast"
            }
          },
          "title": "Podcasts"
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Browse by Genre",
          "limit": 8
        },
        {
          "type": "authors",
          "layout": "horizontal",
          "data": {
            "title": "Popular Authors",
            "list": [],
            "params": {
              "limit": 30
            }
          },
          "title": "Popular Authors"
        }
      ],
      search: [
        {
          "type": "categories",
          "layout": "two_column",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 16
            }
          },
          "title": "Browse by Genre",
          "limit": 16
        }
      ],
      library: [
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "recently_played",
          "data": {
            "title": "Recently Played",
            "list": [],
            "params": {
              "filter[library]": "played"
            }
          },
          "title": "Recently Played"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "liked",
          "data": {
            "title": "Liked",
            "list": [],
            "params": {
              "filter[library]": "liked"
            }
          },
          "title": "Liked"
        },
        {
          "type": "titles",
          "layout": "grid",
          "id": "purchased",
          "data": {
            "title": "My Library",
            "list": [],
            "params": {
              "filter[library]": "purchased"
            }
          },
          "title": "My Library"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "bookmarked",
          "data": {
            "title": "Bookmarked",
            "list": [],
            "params": {
              "filter[library]": "bookmarked"
            }
          },
          "title": "Bookmarked"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "finished",
          "data": {
            "title": "Finished",
            "list": [],
            "params": {
              "filter[library]": "finished"
            }
          },
          "title": "Finished"
        }
      ]
    }
  },

  features: {
    shop: true,
    ads: true,
    guestMode: true,
    favorites: true,
    darkMode: true,
    search: true,
    player: true,
    sharing: true,
    epubReader: false,
    video: false,
    hasPreview: true
  },

  defaultMode: 'dark' as const,

  subscription: {
    stripePlanId: 'price_1UT1juGw3TmAggLVpsd01scJ',
    price: '4.99',
    currency: 'GBP',
    plans: [
      {
        id: 'heard_monthly',
        interval: 'month',
        price: '4.99',
        stripePlanId: 'price_1UT1juGw3TmAggLVpsd01scJ',
        introPrice: '1.00',
        introDays: 14,
      },
      {
        id: 'heard_yearly',
        interval: 'year',
        price: '49.99',
        stripePlanId: 'price_1UT1juGw3TmAggLVhkUhWX59',
      },
    ],
    features: [
      'Limitless Listening',
      'Exclusive and Original Audio',
      'New Content Monthly',
      'Cancel Anytime',
    ],
    usps: [
      {
        title: 'Limitless Listening',
        description:
          'Stream and enjoy our full library on your terms—listen as much as you want, whenever you want.',
        icon: 'headphones',
      },
      {
        title: 'Exclusive and Original Audio',
        description:
          'Subscriber-only titles, originals, and audio you will not find in the free experience.',
        icon: 'star',
      },
      {
        title: 'New Content Monthly',
        description: 'Fresh listens added regularly so there is always something new to discover.',
        icon: 'timer',
      },
      {
        title: 'Cancel Anytime',
        description: 'Stay in control—end your subscription whenever it suits you, no long lock-in.',
        icon: 'minus-circle',
      },
    ]
  },

  urls: {
    terms: 'https://heard.plus/terms-of-use',
    privacy: 'https://heard.plus/privacy-policy',
    support: 'https://heard.plus/support'
  },

  company: {
    legalName: 'Independent Broadcasting Network Limited',
    address: 'West Wing Studios, Unit 166, The Mall, Luton, Bedfordshire, LU1 2TL, UK',
    email: 'info@independentbroadcasting.network',
  },

  website: {
    siteUrl: 'https://heard.plus',
    mode: 'dark',
    menu: [
      { label: 'Home', slug: '' },
      { label: 'About', slug: 'about' },
      { label: 'Contact', slug: 'contact' },
      { label: 'Download', slug: 'download' },
      { label: 'Support', slug: 'support' },
    ],
    cta: {
      label: 'Start Listening',
      href: 'https://app.heard.plus',
    },
    contactEmail: 'info@independentbroadcasting.network',
    stores: {
      apple: 'https://apps.apple.com/gb/app/heard-plus/id6470788524',
      google: 'https://play.google.com/store/apps/details?id=com.hearddigital.heardplus',
    },
    footer: {
      columnOne: [
        { label: 'About', slug: 'about' },
        { label: 'Download', slug: 'download' },
        { label: 'Contact', slug: 'contact' },
      ],
      columnTwo: [
        { label: 'Support', slug: 'support' },
        { label: 'Privacy Policy', slug: 'privacy-policy' },
        { label: 'Terms of use', slug: 'terms-of-use' },
      ],
      social: [
        { label: 'Facebook', href: 'https://www.facebook.com/heardplus', icon: 'facebook' },
        { label: 'Instagram', href: 'https://www.instagram.com/heardplus', icon: 'instagram' },
        { label: 'X', href: 'https://x.com/heardplus', icon: 'x' },
      ],
    },
  },

  seo: {
    title: 'Heard - Your Personal Audio Library',
    description: 'Stream your favorite audiobooks and podcasts',
    ogImage: '/assets/images/heard/og-image.jpg',
    defaults: {
      titles: {
        title: 'Audiobooks | Heard Plus',
        description: 'Explore our collection of premium audiobooks',
        type: 'article'
      },
      categories: {
        title: 'Categories | Heard Plus',
        description: 'Browse audiobooks by genre and category',
        type: 'website'
      },
      authors: {
        title: 'Authors | Heard Plus',
        description: 'Discover talented authors and their works',
        type: 'website'
      },
      series: {
        title: 'Series | Heard Plus',
        description: 'Follow your favorite audiobook series',
        type: 'website'
      },
      casts: {
        title: 'Cast | Heard Plus',
        description: 'Meet the talented narrators behind our audiobooks',
        type: 'website'
      }
    }
  }
} 